import styled from 'styled-components';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  gap: 14px;
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 260px;
  height: 40px;
  padding: 0 14px;
  border-radius: 14px;
  border: 1px solid ${(props) => props.theme.colors.disabled};
  background: ${(props) =>
    props.theme.title === 'light'
      ? props.theme.colors.lightgrey
      : props.theme.colors.card};
  color: ${(props) => props.theme.colors.label};
  font-size: 13px;
  font-weight: 600;
  outline: none;
  transition: border-color 150ms;

  &::placeholder {
    color: ${(props) => props.theme.colors.label};
    opacity: 0.55;
  }

  &:focus {
    border-color: ${(props) => props.theme.colors.lightblue};
  }
`;

// Chips de filtro por tipo. Click toggla — varios podem ficar ativos
// simultaneos (OR). $active controla destaque visual.
export const TypeChip = styled.button`
  height: 30px;
  padding: 0 12px;
  border-radius: 14px;
  border: 1px solid
    ${(props) =>
      props.$active
        ? props.theme.colors.lightblue
        : props.theme.colors.disabled};
  background: ${(props) =>
    props.$active ? props.theme.colors.lightblue : 'transparent'};
  color: ${(props) =>
    props.$active ? '#fff' : props.theme.colors.label};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 150ms, color 150ms, border-color 150ms;

  &:hover {
    border-color: ${(props) => props.theme.colors.lightblue};
  }
`;

export const HintText = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.colors.label};
  opacity: 0.7;
  font-weight: 600;
`;

// Botoes secundarios da toolbar (Baixar Modelo / Importar CSV). Visual
// "ghost" — borda + texto, sem fundo solido — pra nao competir com a
// busca/chips.
export const ToolbarButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: 14px;
  border: 1px solid ${(props) => props.theme.colors.disabled};
  background: transparent;
  color: ${(props) => props.theme.colors.label};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color 150ms, color 150ms;

  &:hover {
    border-color: ${(props) => props.theme.colors.lightblue};
    color: ${(props) => props.theme.colors.lightblue};
  }
`;

export const HiddenFileInput = styled.input.attrs({
  type: 'file',
  accept: '.csv,text/csv',
})`
  display: none;
`;

// Painel inline de resumo do ultimo import. Substitui modal — info e
// curta, fica abaixo da toolbar e o operador dispensa quando termina de
// ler. Cor varia conforme `$variant` (ok/warn/error) — usa paleta de
// status global.
export const ImportSummaryPanel = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid
    ${(props) => {
      if (props.$variant === 'error') return props.theme.colors.red;
      if (props.$variant === 'warn') return props.theme.colors.orange;
      return props.theme.colors.lightblue;
    }};
  background: ${(props) => {
    const op = props.theme.title === 'light' ? 0.08 : 0.14;
    if (props.$variant === 'error') return `rgba(232, 56, 56, ${op})`;
    if (props.$variant === 'warn') return `rgba(245, 158, 11, ${op})`;
    return `rgba(116, 192, 252, ${op})`;
  }};
`;

export const ImportSummaryBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: ${(props) => props.theme.colors.darkerblue};
`;

export const ImportSummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
`;

export const ImportSummaryClose = styled.button`
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: ${(props) => props.theme.colors.label};
  cursor: pointer;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${(props) =>
      props.theme.title === 'light'
        ? 'rgba(0,0,0,0.05)'
        : 'rgba(255,255,255,0.08)'};
  }
`;

// Lista expansivel dos "nao encontrados". Inicia colapsada — opcional
// pra detalhe.
export const NotFoundList = styled.details`
  > summary {
    cursor: pointer;
    font-size: 12px;
    font-weight: 700;
    color: ${(props) => props.theme.colors.label};
    list-style: none;
    padding: 2px 0;
  }
  > summary::-webkit-details-marker {
    display: none;
  }
  > summary::before {
    content: '▸';
    display: inline-block;
    margin-right: 4px;
    transition: transform 150ms;
  }
  &[open] > summary::before {
    transform: rotate(90deg);
  }
`;

export const NotFoundCode = styled.code`
  display: inline-block;
  padding: 2px 8px;
  margin: 2px 4px 2px 0;
  border-radius: 6px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(0,0,0,0.06)'
      : 'rgba(255,255,255,0.1)'};
  font-size: 11px;
  color: ${(props) => props.theme.colors.darkerblue};
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
`;

// Layout dos 2 paineis + coluna central com botoes ← →.
export const Panes = styled.div`
  display: grid;
  grid-template-columns: 1fr 60px 1fr;
  gap: 12px;
  align-items: stretch;
  flex: 1 1 0;
  height: 0;
  min-height: 0;
  overflow: hidden;
`;

export const Pane = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: 1px solid ${(props) => props.theme.colors.disabled};
  border-radius: 8px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(0,0,0,0.02)'
      : 'rgba(255,255,255,0.02)'};
  overflow: hidden;
`;

export const PaneHeader = styled.div`
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${(props) => props.theme.colors.disabled};
  font-size: 13px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.darkerblue};
  background: ${(props) => props.theme.colors.card};
`;

export const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 22px;
  border-radius: 14px;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.label};
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(0,0,0,0.06)'
      : 'rgba(255,255,255,0.1)'};
`;

export const PaneList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 6px;
`;

// Linha de variavel. $selected = clicada (highlight pra bulk move).
export const ListItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  transition: background 100ms;
  background: ${(props) =>
    props.$selected
      ? props.theme.title === 'light'
        ? 'rgba(43, 137, 215, 0.12)'
        : 'rgba(116, 192, 252, 0.18)'
      : 'transparent'};

  &:hover {
    background: ${(props) =>
      props.$selected
        ? props.theme.title === 'light'
          ? 'rgba(43, 137, 215, 0.18)'
          : 'rgba(116, 192, 252, 0.24)'
        : props.theme.title === 'light'
        ? 'rgba(0,0,0,0.04)'
        : 'rgba(255,255,255,0.05)'};
  }
`;

export const ListItemName = styled.span`
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.darkerblue};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Badge de tipo. Cor segue paleta semantica de /variables.
export const TypeTag = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 10px;
  border-radius: 14px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: #fff;
  background: ${(props) => {
    const t = (props.type || '').toLowerCase();
    if (t.includes('alarme') || t.includes('bloqueio'))
      return props.theme.colors.red;
    if (t.includes('configura')) return props.theme.colors.label;
    if (t.includes('comando')) return props.theme.colors.orange;
    return props.theme.colors.blue;
  }};
`;

export const EmptyPane = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px;
  color: ${(props) => props.theme.colors.label};
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  opacity: 0.7;
`;

// Coluna central com os 2 botoes de mover.
export const MoveButtonsCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const MoveButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.colors.disabled};
  background: ${(props) => props.theme.colors.card};
  color: ${(props) => props.theme.colors.label};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: border-color 150ms, color 150ms, background 150ms;

  &:hover:not(:disabled) {
    border-color: ${(props) => props.theme.colors.lightblue};
    color: ${(props) => props.theme.colors.lightblue};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  gap: 12px;
  padding-top: 10px;
  border-top: 1px solid ${(props) => props.theme.colors.disabled};
  background: var(--cco-surface);
`;

export const DiffSummary = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.label};
`;

export const SaveButton = styled.button`
  height: 40px;
  padding: 0 22px;
  border-radius: 10px;
  border: none;
  background: ${(props) => props.theme.colors.blue};
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: opacity 150ms;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 360px;
  color: ${(props) => props.theme.colors.label};
  font-size: 13px;
  font-weight: 600;
`;
